import { captureException } from "@sentry/react";
import type {
	QueryFunctionContext,
	QueryKey,
	UseQueryOptions,
} from "@tanstack/react-query";
import { createElement } from "react";
import { toast } from "sonner";
import type {
	ErrorResponse,
	ErrorStatuses,
	ExceptionDTO,
	FetchArgsOptions,
	FetchResponse,
	FetchResponseOfError,
} from "web_myplann/Infrastructure/Api/Api";

export const appSessionStartedAt = Date.now();

type BaseResponse<TData = unknown> =
	| FetchResponse<TData, number>
	| ErrorResponse;

type SuccessResponse<TResponse extends BaseResponse> = Extract<
	TResponse,
	{ status: 200 | 201 | 202 | 204 }
>;

type SuccessResponseData<TResponse extends BaseResponse> =
	SuccessResponse<TResponse>["data"];

type Statuses400s = 400 | 401 | 403 | 404 | 405 | 409 | 422;

type ProcessResponseOptions = {
	skipToastFor?: (ErrorStatuses | Statuses400s)[] | true;
};

type QueryInfo = {
	meta?: UseQueryOptions["meta"];
	queryKey: QueryKey;
};

export class ApiCallError extends Error {
	data: FetchResponseOfError | FetchResponse<ExceptionDTO, Statuses400s>;
	queryInfo?: QueryInfo;
	options?: ProcessResponseOptions;

	constructor(
		message: string,
		data: FetchResponseOfError,
		options?: ProcessResponseOptions,
		queryKey?: QueryInfo["queryKey"],
		meta?: QueryInfo["meta"],
	) {
		super(message);
		this.name = "ApiCallError";
		this.data = data;
		this.options = options;
		if (queryKey) {
			this.queryInfo = { queryKey, meta };
		}
	}

	protected isApiException = true;
}

class MiddlewareTerminationError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "MiddlewareTerminationError";
	}
}

const isFetchResponseOfError = (obj: any): obj is FetchResponseOfError =>
	!!obj.data && typeof obj.status === "number";

const toastConfig = {
	enableToast: true,
};

export const handleResponseError = (error: any) => {
	if (error instanceof MiddlewareTerminationError) {
		return;
	}

	if (error instanceof ApiCallError && error.data.status === 0) {
		return;
	}

	logExceptionToSentry(error);
	const skipToast =
		error instanceof ApiCallError
			? error.options?.skipToastFor === true ||
				error.options?.skipToastFor?.includes(error.data.status)
			: false;

	const errorMessage = error.message ?? "Unknown error";

	const finalMessage = errorMessage
		.split(/\\n/g)
		.map((msg: string) => `${msg.trim().replace(/\.*$/, "")}.`)
		.join("\n");

	if (finalMessage && !skipToast && toastConfig.enableToast) {
		toast.error(
			createElement("div", { style: { whiteSpace: "pre-line" } }, finalMessage),
			{ id: error.message },
		);
	}
};

function logExceptionToSentry(error: any) {
	if (error instanceof ApiCallError) {
		console.log(JSON.parse(JSON.stringify(error)));

		if (error.data.status === 401) {
			return;
		}
	}

	captureException(error, (scope) => {
		if (error instanceof ApiCallError) {
			const transactionName =
				error.data.args?.options?.method && error.data.args?.url
					? [error.data.args?.options?.method ?? "", error.data.args?.url]
							.filter((a) => !!a)
							.join(" ")
					: undefined;

			if (transactionName) {
				scope.setTransactionName(transactionName);
			}

			scope.setExtra("request_url", error.data.args?.url);
			scope.setExtra("request_method", error.data.args?.options?.method);
			scope.setExtra("response_status", error.data.status);

			if (error.data.data) {
				scope.setExtra(
					"response_body",
					JSON.stringify(error.data.data, null, 2),
				);
			}

			if (error.data.error) {
				scope.setExtra("network_error", {
					name: error.data.error.name,
					message: error.data.error.message,
					stack: error.data.error.stack,
				});
			}

			if (error.data) {
				scope.setExtra("data", error.data);
			}

			if (error.queryInfo) {
				scope.setExtra("queryInfo", error.queryInfo);
			}
		}
		return scope;
	});
}

function getMessageFromErrorResponse(response: any | undefined) {
	let errorMessage: string | undefined;
	let responseError = response;
	if (responseError instanceof ApiCallError) {
		responseError = responseError.data;
	}

	if (isFetchResponseOfError(responseError)) {
		responseError = responseError.data;
	}

	if (responseError?.title?.length > 0 && responseError?.detail?.length > 0) {
		return `${responseError.title}: ${responseError.detail}`;
	}

	if (responseError?.title?.length > 0) {
		return responseError.title;
	}

	if (responseError instanceof Error) {
		errorMessage = responseError.message;
	}

	if (responseError.errors) {
		return undefined;
	}

	return errorMessage ?? "Unknown error";
}

export const processResponseBase =
	<TArgs, TResponse extends BaseResponse>(
		request: (args: TArgs, options?: FetchArgsOptions) => Promise<TResponse>,
	) =>
	async (args: TArgs): Promise<TResponse> => {
		const response = await request(args);
		return processResponseStatusCodeBase(response);
	};

export const processResponse =
	<TArgs, TResponse extends BaseResponse>(
		request: (args: TArgs, options?: FetchArgsOptions) => Promise<TResponse>,
		options?: ProcessResponseOptions,
	) =>
	async (args: TArgs): Promise<SuccessResponseData<TResponse>> => {
		const response = await request(args);
		return processResponseStatusCode(response, options);
	};

export const processResponseQuery =
	<TResponse extends BaseResponse, TQueryKey extends QueryKey = QueryKey>(
		request: (
			args: QueryFunctionContext<TQueryKey>,
			options?: FetchArgsOptions,
		) => Promise<TResponse>,
		options?: ProcessResponseOptions,
	) =>
	async (
		args: QueryFunctionContext<TQueryKey>,
	): Promise<SuccessResponseData<TResponse>> => {
		const response = await request(args);
		return processResponseStatusCode(response, options, args);
	};

function processResponseStatusCodeBase<
	TResponse extends BaseResponse,
	TQueryKey extends QueryKey = QueryKey,
>(
	response: TResponse,
	options?: ProcessResponseOptions,
	args?: QueryFunctionContext<TQueryKey, never>,
): TResponse {
	if (response.error?.message.includes("Request terminated by middleware")) {
		throw new MiddlewareTerminationError(response.args.url);
	}
	if (response?.status >= 400 || response?.status === 0) {
		const message = getMessageFromErrorResponse(response);

		if (response?.status !== 404) {
			throw new ApiCallError(
				message,
				response as FetchResponseOfError,
				options,
				args?.queryKey,
				args?.meta,
			);
		}
	}

	return response;
}

function processResponseStatusCode<
	TResponse extends BaseResponse,
	TQueryKey extends QueryKey = QueryKey,
>(
	response: TResponse,
	options?: ProcessResponseOptions,
	args?: QueryFunctionContext<TQueryKey, never>,
): SuccessResponseData<TResponse> {
	return processResponseStatusCodeBase(response, options, args)?.data;
}

type HandleCallResponse<TData> =
	| {
			isSuccess: true;
			data: TData;
	  }
	| { isSuccess: false; data?: never };

/**
 * Similar to @function processResponse but used to handle manual api calls. It does not throw an error but shows a toast message when status is not 2xx.
 */
export const handleCall = async <TResponse extends BaseResponse>(
	request: Promise<TResponse>,
	options?: ProcessResponseOptions,
): Promise<HandleCallResponse<SuccessResponseData<TResponse>>> => {
	try {
		const response = await request;
		const data = processResponseStatusCode(response, options);
		return { isSuccess: true, data };
	} catch (error) {
		handleResponseError(error);
	}
	return { isSuccess: false };
};
